import { useCallback, useEffect, useMemo, useState } from "react";
import { useLang } from "../hooks/useLang";
import {
  listBillingAddresses,
  createBillingAddress,
  deleteBillingAddress,
  type BillingAddress,
  type BillingAddressInput,
} from "../services/billing";
import { TR_PROVINCE_NAMES, ilcelerForIl } from "../lib/trGeo";
import Icon from "./icons";

type AddrType = "bireysel" | "kurumsal";
type SubTab = "list" | "add";

const EMPTY: BillingAddressInput = {
  title: "",
  address_type: "kurumsal",
  first_name: "",
  last_name: "",
  tc_kimlik_no: "",
  company_name: "",
  tax_office: "",
  tax_no: "",
  country: "Türkiye",
  state: "",
  city: "",
  postal_code: "",
  email: "",
  phone: "",
  full_address: "",
};

export default function BillingPanel() {
  const { t } = useLang();
  const [items, setItems] = useState<BillingAddress[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<BillingAddressInput>(EMPTY);
  const [sub, setSub] = useState<SubTab>("list");

  const set = <K extends keyof BillingAddressInput>(k: K, v: BillingAddressInput[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const districts = useMemo(() => ilcelerForIl(form.state || ""), [form.state]);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      setItems(await listBillingAddresses());
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const onAdd = useCallback(async () => {
    if (!form.title.trim()) { setErr(t("bill_title_required")); return; }
    setCreating(true);
    setErr(null);
    try {
      await createBillingAddress(form);
      setForm(EMPTY);
      await load();
      setSub("list"); // kayıt sonrası listeye dön — eklenen adres görünsün
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setCreating(false);
    }
  }, [form, load, t]);

  const onDelete = useCallback(async (b: BillingAddress) => {
    if (!window.confirm(t("bill_delete_confirm").replace("{title}", b.title))) return;
    setErr(null);
    try {
      await deleteBillingAddress(b.id);
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }, [load, t]);

  const setType = (ty: AddrType) => set("address_type", ty);

  return (
    <>
      <p className="hint">{t("billing_intro")}</p>

      {err && <div className="error">{t("err_prefix")}: {err}</div>}

      {/*
        Alt sekmeler. Eskiden ekleme formu ile liste ALT ALTA tek sayfadaydı;
        form uzun olduğu için mevcut adresleri görmek epey aşağı kaydırmayı
        gerektiriyordu (müşteri bulgusu 2026-07-30). Varsayılan LİSTE: sayfaya
        girenlerin çoğu mevcut kaydı görmek/seçmek için geliyor, yeni adres
        eklemek daha seyrek bir iş.
      */}
      <div className="settings-tabs subtabs" role="tablist">
        <button
          type="button" role="tab" aria-selected={sub === "list"}
          className={"settings-tab" + (sub === "list" ? " is-active" : "")}
          onClick={() => setSub("list")}
        >
          <Icon name="receipt" size={15} /> {t("billing_list_title")}
        </button>
        <button
          type="button" role="tab" aria-selected={sub === "add"}
          className={"settings-tab" + (sub === "add" ? " is-active" : "")}
          onClick={() => setSub("add")}
        >
          <Icon name="plus" size={15} /> {t("bill_add_title")}
        </button>
      </div>

      {sub === "add" && <section className="settings-section">
        <h3>{t("bill_add_title")}</h3>

        <label className="form-row">
          <span>{t("bill_title_label")}</span>
          <input type="text" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder={t("bill_title_ph")} />
        </label>

        <label className="form-row">
          <span>{t("bill_type_label")}</span>
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" className={form.address_type === "bireysel" ? "btn-pri" : "btn-sec"} onClick={() => setType("bireysel")}>
              {t("bill_type_bireysel")}
            </button>
            <button type="button" className={form.address_type === "kurumsal" ? "btn-pri" : "btn-sec"} onClick={() => setType("kurumsal")}>
              {t("bill_type_kurumsal")}
            </button>
          </div>
        </label>

        {form.address_type === "bireysel" ? (
          <>
            <label className="form-row">
              <span>{t("bill_first_name")}</span>
              <input type="text" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} />
            </label>
            <label className="form-row">
              <span>{t("bill_last_name")}</span>
              <input type="text" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} />
            </label>
            <label className="form-row">
              <span>{t("bill_tc")}</span>
              <input type="text" inputMode="numeric" maxLength={11} value={form.tc_kimlik_no} onChange={(e) => set("tc_kimlik_no", e.target.value)} />
            </label>
          </>
        ) : (
          <>
            <label className="form-row">
              <span>{t("bill_company")}</span>
              <input type="text" value={form.company_name} onChange={(e) => set("company_name", e.target.value)} />
            </label>
            <label className="form-row">
              <span>{t("bill_tax_office")}</span>
              <input type="text" value={form.tax_office} onChange={(e) => set("tax_office", e.target.value)} />
            </label>
            <label className="form-row">
              <span>{t("bill_tax_no")}</span>
              <input type="text" inputMode="numeric" value={form.tax_no} onChange={(e) => set("tax_no", e.target.value)} />
            </label>
          </>
        )}

        <label className="form-row">
          <span>{t("bill_country")}</span>
          <input type="text" value={form.country} readOnly />
        </label>

        <label className="form-row">
          <span>{t("bill_province")}</span>
          <select
            value={form.state}
            onChange={(e) => setForm((f) => ({ ...f, state: e.target.value, city: "" }))}
          >
            <option value="">{t("bill_select_province")}</option>
            {TR_PROVINCE_NAMES.map((il) => <option key={il} value={il}>{il}</option>)}
          </select>
        </label>

        <label className="form-row">
          <span>{t("bill_district")}</span>
          <select value={form.city} disabled={!form.state} onChange={(e) => set("city", e.target.value)}>
            <option value="">{t("bill_select_district")}</option>
            {districts.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </label>

        <label className="form-row">
          <span>{t("bill_postal")}</span>
          <input type="text" inputMode="numeric" maxLength={5} value={form.postal_code} onChange={(e) => set("postal_code", e.target.value)} />
        </label>

        <label className="form-row">
          <span>{t("bill_email")}</span>
          <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} />
        </label>

        <label className="form-row">
          <span>{t("bill_phone")}</span>
          <input type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+90" />
        </label>

        <label className="form-row">
          <span>{t("bill_address")}</span>
          <textarea rows={3} value={form.full_address} onChange={(e) => set("full_address", e.target.value)} />
        </label>

        <div className="settings-actions">
          <button className="btn-pri" onClick={onAdd} disabled={creating || !form.title.trim()}>
            {creating ? t("saving") : t("bill_add_btn")}
          </button>
        </div>
      </section>}

      {sub === "list" && <section className="settings-section">
        <h3>{t("billing_list_title")}</h3>
        {loading && <div className="hint">{t("billing_loading")}</div>}
        {!loading && items.length === 0 && <div className="hint">{t("billing_empty")}</div>}

        <div style={{ display: "grid", gap: 8 }}>
          {items.map((b) => (
            <div key={b.id} style={{
              display: "flex", alignItems: "flex-start", gap: 12, flexWrap: "wrap",
              padding: "10px 12px", border: "1px solid var(--border)", borderRadius: 6,
            }}>
              <div style={{ flex: "1 1 280px", minWidth: 0 }}>
                <div style={{ fontWeight: 500 }}>
                  {b.title} <span className="badge neutral">{b.address_type === "bireysel" ? t("bill_type_bireysel") : t("bill_type_kurumsal")}</span>
                </div>
                <div className="hint" style={{ fontSize: 12.5 }}>
                  {b.address_type === "bireysel"
                    ? [b.first_name, b.last_name].filter(Boolean).join(" ")
                    : b.company_name}
                  {b.tax_no ? ` · ${b.tax_no}` : ""}
                  {b.tc_kimlik_no ? ` · ${b.tc_kimlik_no}` : ""}
                </div>
                <div className="hint" style={{ fontSize: 12 }}>
                  {[b.full_address, b.city, b.state, b.postal_code].filter(Boolean).join(", ")}
                </div>
              </div>
              <button className="btn-sec" onClick={() => onDelete(b)}>{t("delete")}</button>
            </div>
          ))}
        </div>
      </section>}
    </>
  );
}
